
import React, { useState } from 'react';
import { MapPin, Clock, Phone, ChevronLeft, ChevronRight, Calendar, Navigation, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openGoogleCalendar } from "@/lib/utils";

interface ConsultorioData {
  id: number;
  consultorioId: string;
  name: string;
  address: string;
  city: string;
  cep: string;
  complement: string;
  phone: string;
  googleMapsUrl: string;
  agendamentoUrl?: string;
  badgeText?: string;
  horarios: string[];
  transporte: string[];
  servicos: string[];
  destaces: string[];
  photos: string[];
}

interface ConsultorioCardProps {
  consultorio: ConsultorioData;
  index: number;
  onCall: (phone: string) => void;
}

export const ConsultorioCard: React.FC<ConsultorioCardProps> = ({
  consultorio,
  index,
  onCall,
}) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = consultorio.photos ?? [];
  const prev = () => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setPhotoIndex((i) => (i + 1) % photos.length);
  const isReversed = index % 2 === 1;

  return (
    <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col lg:flex-row">

      {/* ── Foto carousel ─────────────────────────────────── */}
      <div className={`relative w-full lg:w-[420px] lg:shrink-0 ${isReversed ? 'lg:order-2' : ''}`}>
        <div className="relative aspect-square w-full overflow-hidden">
          {photos.length > 0 ? (
            <>
              <img
                src={photos[photoIndex]}
                alt={`${consultorio.name} - foto ${photoIndex + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* gradient overlay bottom */}
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent" />

              {photos.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/65 text-white rounded-full p-2 transition-colors" aria-label="Foto anterior">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/65 text-white rounded-full p-2 transition-colors" aria-label="Próxima foto">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {photos.map((_, i) => (
                      <button key={i} onClick={() => setPhotoIndex(i)} className={`w-2 h-2 rounded-full transition-all duration-200 ${i === photoIndex ? 'bg-white scale-110' : 'bg-white/50'}`} aria-label={`Foto ${i + 1}`} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/20 to-brand-primary/5 flex items-center justify-center">
              <MapPin className="w-16 h-16 text-brand-primary/30" />
            </div>
          )}
        </div>
      </div>

      {/* ── Painel de informações ─────────────────────────── */}
      <div className={`flex-1 flex flex-col bg-white ${isReversed ? 'lg:order-1' : ''}`}>

        {/* cabeçalho verde */}
        <div className="bg-brand-primary px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white font-primary leading-tight">
              {consultorio.name}
            </h2>
            <p className="text-white/75 text-sm mt-0.5 font-primary">{consultorio.city}</p>
          </div>
          {(consultorio.badgeText ?? 'Verificado') && (
            <span className="shrink-0 flex items-center gap-1 bg-white/15 text-white text-xs font-medium px-3 py-1.5 rounded-full mt-0.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> {consultorio.badgeText ?? 'Verificado'}
            </span>
          )}
        </div>

        {/* corpo */}
        <div className="flex-1 flex flex-col gap-5 px-6 py-5">

          {/* endereço + contato + horário */}
          <div className="space-y-2.5 text-sm text-gray-600">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" />
              <div>
                <span>{consultorio.address}</span>
                {consultorio.complement && <span> – {consultorio.complement}</span>}
                <br />
                <span className="text-gray-400">{consultorio.city} · CEP {consultorio.cep}</span>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Phone className="w-4 h-4 text-brand-primary shrink-0" />
              <a href={`tel:${consultorio.phone.replace(/\D/g, '')}`} className="text-brand-primary font-semibold hover:underline">
                {consultorio.phone}
              </a>
            </div>
            <div className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                {consultorio.horarios.map((h, i) => <p key={i}>{h}</p>)}
              </div>
            </div>
          </div>

          {/* divisor */}
          <div className="border-t border-gray-100" />

          {/* serviços + destaques */}
          <div className="space-y-3">
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Serviços</p>
              <div className="flex flex-wrap gap-1.5">
                {consultorio.servicos.map((s) => (
                  <span key={s} className="text-xs px-3 py-1 rounded-full bg-brand-primary/8 text-brand-primary font-medium border border-brand-primary/15">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Destaques</p>
              <div className="flex flex-wrap gap-1.5">
                {consultorio.destaces.map((d) => (
                  <span key={d} className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* spacer */}
          <div className="flex-1" />

          {/* ações */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <Button
              className="col-span-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-primary shadow-md"
              onClick={consultorio.agendamentoUrl ? () => window.open(consultorio.agendamentoUrl, '_blank') : openGoogleCalendar}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Agendar Consulta
            </Button>
            <Button
              variant="outline"
              className="border-gray-200 text-gray-600 hover:bg-gray-50 font-primary"
              onClick={() => window.open(consultorio.googleMapsUrl, '_blank')}
            >
              <Navigation className="w-4 h-4 mr-1.5" />
              Mapa
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};
